import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Landmark, RefreshCw, FileDown, CheckCircle2, XCircle, AlertTriangle, Loader2, Send, Inbox } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import { formatDate } from '../lib/formatDate';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATO_STYLE = {
  consegnata: 'text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
  accettata: 'text-emerald-600 border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30',
  inviata: 'text-sky-600 border-sky-500 bg-sky-50 dark:bg-sky-950/30',
  scartata: 'text-red-600 border-red-500 bg-red-50 dark:bg-red-950/30',
  rifiutata: 'text-red-600 border-red-500 bg-red-50 dark:bg-red-950/30',
};

function StatoSdiBadge({ stato }) {
  if (!stato) return <span className="text-muted-foreground text-xs">-</span>;
  const key = String(stato).toLowerCase();
  const style = STATO_STYLE[key] || (key.includes('mancata') ? 'text-amber-600 border-amber-500 bg-amber-50 dark:bg-amber-950/30' : 'text-slate-600 border-slate-400');
  return <Badge variant="outline" className={`text-[10px] uppercase ${style}`}>{stato}</Badge>;
}

function BannerStato({ stato, loading }) {
  if (loading) {
    return (
      <Card className="border-border">
        <CardContent className="py-4 flex items-center gap-3 text-sm text-muted-foreground" data-testid="aruba-stato-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Verifica connessione ad Aruba in corso…
        </CardContent>
      </Card>
    );
  }
  if (!stato) return null;
  if (stato.ws_ok) {
    return (
      <Card className="border-emerald-500/40 bg-emerald-50/50 dark:bg-emerald-950/20">
        <CardContent className="py-4 flex items-center gap-3 text-sm" data-testid="aruba-stato-ok">
          <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
          <div>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">Connesso ad Aruba Fatturazione Elettronica</span>
            <span className="text-muted-foreground ml-2">Ambiente: {stato.ambiente}</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card className={stato.auth_ok ? 'border-amber-500/50 bg-amber-50/50 dark:bg-amber-950/20' : 'border-red-500/50 bg-red-50/50 dark:bg-red-950/20'}>
      <CardContent className="py-4 flex items-start gap-3 text-sm" data-testid="aruba-stato-errore">
        {stato.auth_ok
          ? <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          : <XCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />}
        <div className="space-y-1">
          <p className="font-semibold">
            {stato.auth_ok
              ? 'Credenziali Aruba valide, ma i Web Service non sono abilitati'
              : 'Connessione ad Aruba non riuscita'}
          </p>
          <p className="text-muted-foreground">{stato.errore}</p>
          {stato.auth_ok && (
            <p className="text-muted-foreground">
              Appena Aruba abilita le deleghe Web Service sull'account, questa pagina e il pulsante
              &quot;Invia a SdI&quot; nei Documenti funzioneranno automaticamente, senza altre modifiche.
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TabellaFatture({ righe, direzione, loading, onDownload }) {
  if (loading) {
    return <div className="py-10 text-center text-muted-foreground text-sm"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento fatture…</div>;
  }
  return (
    <div className="border border-border rounded-md overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Numero</th>
            <th className="hidden md:table-cell">Data</th>
            <th>{direzione === 'out' ? 'Cliente' : 'Fornitore'}</th>
            <th className="hidden lg:table-cell">P.IVA</th>
            <th>Stato SdI</th>
            <th>File</th>
          </tr>
        </thead>
        <tbody>
          {righe.length === 0 ? (
            <tr><td colSpan={6} className="text-center text-muted-foreground py-8">Nessuna fattura trovata nel periodo selezionato</td></tr>
          ) : righe.map((f) => (
            <tr key={f.filename || `${f.numero}-${f.data}`} className="hover:bg-muted transition-colors">
              <td className="font-mono font-bold">{f.numero || '-'}</td>
              <td className="hidden md:table-cell font-mono text-xs">{f.data ? formatDate(f.data) : '-'}</td>
              <td className="max-w-[240px] truncate">{f.controparte || '-'}</td>
              <td className="hidden lg:table-cell font-mono text-xs">{f.piva_controparte || '-'}</td>
              <td><StatoSdiBadge stato={f.stato} /></td>
              <td>
                {f.filename && (
                  <Button variant="ghost" size="icon" className="h-8 w-8 rounded-md text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/30" onClick={() => onDownload(f.filename)} title="Scarica XML" data-testid={`aruba-download-${f.filename}`}>
                    <FileDown className="h-4 w-4" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function FatturazionePage() {
  const oggi = new Date();
  const unAnnoFa = new Date(oggi); unAnnoFa.setFullYear(oggi.getFullYear() - 1);
  const [stato, setStato] = useState(null);
  const [statoLoading, setStatoLoading] = useState(true);
  const [tab, setTab] = useState('inviate');
  const [dataDa, setDataDa] = useState(unAnnoFa.toISOString().slice(0, 10));
  const [dataA, setDataA] = useState(oggi.toISOString().slice(0, 10));
  const [inviate, setInviate] = useState([]);
  const [ricevute, setRicevute] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchStato = useCallback(async () => {
    setStatoLoading(true);
    try {
      const res = await axios.get(`${API}/aruba/stato`);
      setStato(res.data);
      return res.data;
    } catch (e) {
      toast.error(parseApiError(e, 'Errore verifica connessione Aruba'));
      return null;
    } finally {
      setStatoLoading(false);
    }
  }, []);

  const fetchFatture = useCallback(async () => {
    setLoading(true);
    try {
      const params = { data_da: dataDa, data_a: dataA, size: 50 };
      const [inv, ric] = await Promise.all([
        axios.get(`${API}/aruba/inviate`, { params }),
        axios.get(`${API}/aruba/ricevute`, { params }),
      ]);
      setInviate(inv.data.fatture || []);
      setRicevute(ric.data.fatture || []);
    } catch (e) {
      toast.error(parseApiError(e, 'Errore caricamento fatture da Aruba'), { duration: 8000 });
    } finally {
      setLoading(false);
    }
  }, [dataDa, dataA]);

  useEffect(() => {
    fetchStato().then((s) => { if (s?.ws_ok) fetchFatture(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownload = async (filename) => {
    const direzione = tab === 'inviate' ? 'out' : 'in';
    try {
      const res = await axios.get(`${API}/aruba/download/${direzione}/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(parseApiError(e, 'Errore download file'));
    }
  };

  return (
    <div className="space-y-6" data-testid="fatturazione-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Landmark className="h-6 w-6 text-primary" /> Fatturazione Elettronica
          </h1>
          <p className="text-sm text-muted-foreground">Fatture inviate e ricevute tramite Aruba / Sistema di Interscambio</p>
        </div>
        <Button variant="outline" onClick={() => fetchStato().then((s) => { if (s?.ws_ok) fetchFatture(); })} data-testid="aruba-refresh-btn">
          <RefreshCw className="h-4 w-4 mr-2" /> Aggiorna
        </Button>
      </div>

      <BannerStato stato={stato} loading={statoLoading} />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <div>
              <CardTitle className="text-base">Periodo</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Filtra le fatture per data</p>
            </div>
            <div className="flex items-center gap-2 sm:ml-auto">
              <Input type="date" value={dataDa} onChange={(e) => setDataDa(e.target.value)} className="w-[150px]" data-testid="aruba-data-da" />
              <span className="text-muted-foreground text-sm">→</span>
              <Input type="date" value={dataA} onChange={(e) => setDataA(e.target.value)} className="w-[150px]" data-testid="aruba-data-a" />
              <Button size="sm" onClick={fetchFatture} disabled={!stato?.ws_ok || loading} data-testid="aruba-cerca-btn">Cerca</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="inviate" data-testid="tab-inviate">
                <Send className="h-4 w-4 mr-2" /> Inviate ({inviate.length})
              </TabsTrigger>
              <TabsTrigger value="ricevute" data-testid="tab-ricevute">
                <Inbox className="h-4 w-4 mr-2" /> Ricevute ({ricevute.length})
              </TabsTrigger>
            </TabsList>
            <TabsContent value="inviate" className="mt-4">
              <TabellaFatture righe={inviate} direzione="out" loading={loading} onDownload={handleDownload} />
            </TabsContent>
            <TabsContent value="ricevute" className="mt-4">
              <p className="text-xs text-muted-foreground mb-3">
                Le fatture dei fornitori arrivano qui solo se inviate al codice destinatario Aruba
                (<span className="font-mono font-semibold">KRRH6B9</span>) o se questo è registrato come indirizzo
                telematico predefinito sul portale Fatture e Corrispettivi dell'Agenzia delle Entrate.
              </p>
              <TabellaFatture righe={ricevute} direzione="in" loading={loading} onDownload={handleDownload} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
